
import { h } from 'preact'
import { memo, useRef, useState } from 'preact/compat'
import PropTypes from 'prop-types'

import {
  useMyOrganizations,
  useMyPublicProfile,
  usePublicProfiles,
} from 'lib/membershipAppStateHooks'

import { useChangeChatChannelAttributes } from 'lib/chatHooks'
import { useAppState } from 'lib/appState'
import Button from 'components/Button'
import Icon from 'components/Icon'
import ChatChannelAvatar from 'components/ChatChannelAvatar'
import EndUserAvatar from 'components/EndUserAvatar'
import EndUserDMAvatar from 'components/EndUserDMAvatar'
import SearchInput from 'components/SearchInput'
import TimeAgo from 'components/TimeAgo'
import NotificationBadge from 'components/NotificationBadge'
import './index.sass'

const MEDIA_ATTACHMENT_PREFIX = 'jlinc:dm:attachment:'

const ChatNotificationsList = memo(({ synopsis = [], onMemberClick, openChatConversation }) => {
  const { myPublicProfile } = useMyPublicProfile('ChatNotificationsList')
  const { myOrganizations } = useMyOrganizations('ChatNotificationsList')
  const { changeChatChannelAttributes } = useChangeChatChannelAttributes('ChatNotificationsList')
  const { takeAction } = useAppState({}, 'ChatNotificationsList')
  const containerRef = useRef(null)

  const [markedAsRead, setMarkedAsRead] = useState(new Set())
  const [searchParam, setSearchParam] = useState("")

  // Get all member user DIDs for fetching public profiles
  const allMemberUserDids = synopsis?.flatMap((chat) => chat.memberUserDids || []) || []
  const uniqueMemberUserDids = Array.from(new Set(allMemberUserDids))

  const { publicProfiles } = usePublicProfiles(uniqueMemberUserDids, 'ChatNotificationsList')

  // Filter chats based on search
  const filteredChats = synopsis?.filter(chat => {
    if (!searchParam) return true
    const searchLower = searchParam.toLowerCase()
    return (
      chat.name?.toLowerCase().includes(searchLower) ||
      chat.displayName?.toLowerCase().includes(searchLower) ||
      chat.preview?.toLowerCase().includes(searchLower)
    )
  }) || []

  // Sort chats by latest activity
  const sortedChats = filteredChats.sort((a, b) => {
    const timeA = new Date(a.latestAt || a.createdAt || 0)
    const timeB = new Date(b.latestAt || b.createdAt || 0)
    return timeB - timeA
  })

  const unreadChats = synopsis.filter(chat =>
    (chat.type === 'public_hub_chat' && (chat.unread > 0 || chat.is_unread)) ||
    (!markedAsRead.has(chat.uid) &&
     (chat.unread > 0 || chat.is_unread) &&
     !chat.is_notifications_muted)
  )


  const publicProfileDids = new Set()
  unreadChats.forEach(chatChannel =>
    (chatChannel.memberUserDids || []).forEach(did => publicProfileDids.add(did))
  )
  // Merge with the already fetched publicProfiles for filtered chats
  uniqueMemberUserDids.forEach(did => publicProfileDids.add(did))

  const senderPublicProfileDids = new Set()
  unreadChats.map(chatChannel => {
    if(chatChannel.senderUserDid)
      senderPublicProfileDids.add(chatChannel.senderUserDid)
  })
  const { publicProfiles: senderProfiles } = usePublicProfiles(senderPublicProfileDids, 'ChatNotificationsList')

  const markIndividualMessagesAsRead = async (chatUid) => {
    try {
      await takeAction('chat.markAllChatMessagesAsRead', chatUid)
      await changeChatChannelAttributes(chatUid, { is_unread: false })
      const synopsisIndex = synopsis.findIndex(s => s.uid === chatUid)
      if (synopsisIndex >= 0) {
        synopsis[synopsisIndex].unread = 0
        synopsis[synopsisIndex].is_unread = false
      }
      return true
    } catch (error) {
      console.error(`Error marking messages as read for chat ${chatUid}:`, error)
      return false
    }
  }

  const handleMarkAllAsRead = async () => {
    const newMarkedAsRead = new Set(markedAsRead)
    const markPromises = unreadChats.map(async (chat) => {
      newMarkedAsRead.add(chat.uid)
      await markIndividualMessagesAsRead(chat.uid)
    })
    await Promise.all(markPromises)
    setMarkedAsRead(newMarkedAsRead)
  }

  const handleNotificationClick = async (chatChannel) => {
    setMarkedAsRead(prev => {
      const newSet = new Set(prev)
      newSet.add(chatChannel.uid)
      return newSet
    })
    await markIndividualMessagesAsRead(chatChannel.uid)
    if (onMemberClick) onMemberClick()

    // Check if current URL contains "chat" - if so, navigate to the chat link
    if (window.location.pathname.includes('/chat')) {
      // Navigate to the chat page
      const chatUrl = `/chat/${chatChannel.uid}`
      window.location.href = chatUrl
    } else if (openChatConversation) {
      // Use floating chat functionality if not on a chat page
      openChatConversation(chatChannel)
    } else {
      // Fallback to original navigation behavior
      window.location.href = `/chat/${chatChannel.uid}`
    }
  }

  const stripHTML = (html) => {
    if (!html) return ''
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent.trim()
  }

  const processPreview = (preview, senderProfile) => {
    if (!preview) return ''

    if (preview.startsWith(MEDIA_ATTACHMENT_PREFIX)) {
      const match = preview.match(/"type":"([^"]+)"/)
      return match ? `sent ${RegExp.$1}` : 'sent media attachment'
    }

    const cleanText = stripHTML(preview)
    const truncated = cleanText.length > 27 ? cleanText.substring(0, 27) + '...' : cleanText

    return senderProfile ? `${senderProfile.displayName || senderProfile.username}: ${truncated}` : truncated
  }

  return (
    <div className="ChatNotificationsList">
      <div className="ChatNotificationsList-header">
        <div className="ChatNotificationsList-title">Chat Notification</div>
        <Button
          className="see-all-chats"
          type="primary"
          size="sm"
          href="/chat"
        >
          See All Chats
        </Button>
      </div>

      <div className="ChatNotificationsList-search">
        <SearchInput
          placeholder="Search chats..."
          value={searchParam}
          onChange={setSearchParam}
          className="ChatNotificationsList-searchInput"
        />
      </div>

      {unreadChats.length > 0 && (
        <div className="ChatNotificationsList-action" onClick={handleMarkAllAsRead}>
          <Button
            className="mark-all-read"
            size="sm"
            plain
          >
            <Icon type="read" size="sm" />
            <span>Mark all Read</span>
          </Button>
        </div>
      )}

      <div
        className="ChatNotificationsList-items"
        ref={containerRef}
        style={{ maxHeight: 'calc(4.5 * 64px)' }}
      >
        {sortedChats.length > 0 ? (
          sortedChats.map(chat => (
            <ChatListItem
              key={chat.uid}
              chat={chat}
              publicProfiles={publicProfiles}
              senderProfiles={senderProfiles}
              myPublicProfile={myPublicProfile}
              myOrganizations={myOrganizations}
              onClick={() => handleNotificationClick(chat)}
              processPreview={processPreview}
            />
          ))
        ) : (
          <div className="ChatNotificationsList-empty">
            {searchParam ? 'No chats found' : 'No chats yet'}
          </div>
        )}
      </div>
    </div>
  )
})

function ChatListItem({
  chat,
  publicProfiles,
  senderProfiles,
  myPublicProfile,
  myOrganizations,
  onClick,
  processPreview
}) {
  const isDM = chat.type === 'direct_chat' || chat.type === 'dm'
  const isUnread = (chat.unread || 0) > 0

  // Get chat-specific public profiles if memberUserDids exist
  const chatPublicProfiles = chat.memberUserDids ?
    publicProfiles.filter((profile) => chat.memberUserDids.includes(profile.did)) : []

  const organization = myOrganizations.find(o =>
    o.apikey === chat.organizationApikey
  ) || {apikey: chat.organizationApikey}

  const isGeneral = chat.type === 'public_hub_chat'
  const isGroupChat = chat.isGroupChat ||
    (chat.memberUserDids && chat.memberUserDids.length > 2)

  const otherPublicProfiles = (chat.memberUserDids || [])
    .filter(did => did !== myPublicProfile?.did)
    .map(did => publicProfiles.find(p => p.did === did))
    .filter(Boolean)

  const senderProfile = senderProfiles?.find(p => p.did === chat.senderUserDid)

  let displayName

  const renderAvatar = () => {
    // If chat has memberUserDids and we have public profiles, use EndUserDMAvatar
    if (chat.memberUserDids && chatPublicProfiles.length > 0) {
      return (
        <EndUserDMAvatar
          chatImage={chat.imageUrl || chat.image_url}
          publicProfiles={chatPublicProfiles}
          size="sm"
        />
      )
    }

    // If chat has an image URL, render it directly
    if (chat.imageUrl || chat.image_url) {
      const imageUrl = chat.imageUrl || chat.image_url

      if (imageUrl.startsWith("#")) {
        return (
          <div
            className="avatarImg"
            style={{
              backgroundColor: imageUrl,
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon type="chaty" />
          </div>
        )
      } else {
        return (
          <img
            src={imageUrl}
            alt="Chat Image"
            className="avatarImg"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        )
      }
    }

    // Handle specific chat types
    if (isGeneral) {
      return (
        <div className="chat-hashtag">
          <Icon type="hashtag" size="sm" />
        </div>
      )
    } else if (isGroupChat) {
      return (
        <div className="chat-hashtag">
          <Icon type="hashtagGroup" size="sm" />
        </div>
      )
    } else if (isDM) {
      return <EndUserAvatar size="sm" />
    } else {
      return <ChatChannelAvatar chatChannel={chat} size="sm" />
    }
  }

  if (isGeneral) {
    displayName = `${organization.name || organization.apikey || ''} - General`
  } else if (isGroupChat) {
    displayName = chat.name || 'Group Chat'
  } else {
    const otherMemberProfile = otherPublicProfiles[0]
    displayName = otherMemberProfile?.displayName ||
                  otherMemberProfile?.username ||
                  chat.name ||
                  chat.displayName ||
                  'Chat'
  }

  const lastMessageText = processPreview(chat.preview, senderProfile)
  const lastMessageTime = chat.latestAt || chat.createdAt

  return (
    <div
      className={`ChatNotificationsList-item ${isUnread ? 'unread' : ''}`}
      onClick={onClick}
      data-chat-uid={chat.uid}
      data-chat-type={isGeneral ? 'public_hub_chat' : (isGroupChat ? 'group' : 'direct')}
    >
      {isUnread && (
        <div className="notification-indicator" />
      )}

      <div className="ChatNotificationsList-item-avatar">
        {renderAvatar()}
      </div>

      <div className="ChatNotificationsList-item-content">
        <div className="ChatNotificationsList-item-header">
          <div className="ChatNotificationsList-item-name">
            {displayName?.length > 30 ? displayName.substring(0, 30) + '...' : displayName}
          </div>
          {lastMessageTime && (
            <div className="ChatNotificationsList-item-time">
              <TimeAgo time={lastMessageTime} />
            </div>
          )}
        </div>

        <div className="ChatNotificationsList-item-message">
          {lastMessageText}
        </div>
      </div>

      {isUnread && (chat.unread || 0) > 0 && (
        <div className="ChatNotificationsList-item-badge">
          <NotificationBadge count={chat.unread || 0} />
        </div>
      )}
    </div>
  )
}

ChatNotificationsList.propTypes = {
  synopsis: PropTypes.array,
  onMemberClick: PropTypes.func,
  openChatConversation: PropTypes.func,
}

export default ChatNotificationsList
